/**
 * FormRenderer — Form Rendering Engine
 *
 * Accepts a FormConfig JSON (generated by FormGenie) and renders a fully
 * functional, stateful, validated form. Does NOT own any API logic — all
 * side effects are delegated to the handler props.
 *
 * Reusable for PAL / external embedding: no Redux or app-specific imports.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import type { FormConfig, FormField } from './formgenie-types';
import { getPalFormUrl } from './formgenie-utils';

// ---------------------------------------------------------------------------
// Public event shapes
// ---------------------------------------------------------------------------

export interface UploadedFile {
  fileId: string;
  file: File;
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

export interface UploadFileEvent {
  file: File;
  metadata: { name: string; size: number; type: string; lastModified: number };
  formState: Record<string, unknown>;
}

export interface RemoveFileEvent {
  fileId: string;
  formState: Record<string, unknown>;
}

export interface SubmitFormEvent {
  formState: Record<string, unknown>;
  attachments: UploadedFile[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FormRendererProps {
  config: FormConfig;
  uploadFile: (event: UploadFileEvent) => void;
  removeFile: (event: RemoveFileEvent) => void;
  submitForm: (event: SubmitFormEvent) => void;
  /** When provided, a Share button appears that copies the PAL form link to clipboard. */
  usageId?: string;
  /**
   * When true, hides the Edit / Preview toggle and Share button from the header.
   * A "Preview" button appears before Submit instead, opening a PDF-style
   * summary overlay of the filled form data and attachments.
   * Use this for user-facing PAL embed contexts.
   */
  hideActions?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the appropriate empty/default value for a field type. */
function getDefaultFieldValue(field: Pick<FormField, 'fieldType'>): unknown {
  if (field.fieldType === 'toggle') return false;
  if (
    field.fieldType === 'multiselect' ||
    field.fieldType === 'tags' ||
    field.fieldType === 'repeatable_group'
  ) return [];
  return '';
}

function initFormState(config: FormConfig): Record<string, unknown> {
  const state: Record<string, unknown> = {};
  for (const section of config.sections ?? []) {
    for (const field of section.fields ?? []) {
      state[field.schemaPath] = getDefaultFieldValue(field);
    }
  }
  return state;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// ---------------------------------------------------------------------------
// Preview helpers
// ---------------------------------------------------------------------------

/** Converts a raw form field value to a human-readable string for preview/print. */
function getDisplayValue(field: FormField, value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (field.fieldType === 'toggle') return Boolean(value) ? 'Yes' : 'No';
  if (field.fieldType === 'select') {
    const opt = (field.options ?? []).find((o) => o.value === String(value));
    return opt?.label ?? String(value);
  }
  if ((field.fieldType === 'multiselect' || field.fieldType === 'tags') && Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (field.fieldType === 'multiselect') {
      return value.map((v) => {
        const opt = (field.options ?? []).find((o) => o.value === String(v));
        return opt?.label ?? String(v);
      }).join(', ');
    }
    return (value as string[]).join(', ');
  }
  return String(value);
}

/** Generates a self-contained HTML document for print-to-PDF of form submission data. */
function generatePrintHtml(
  config: FormConfig,
  formState: Record<string, unknown>,
  attachments: UploadedFile[]
): string {
  const sections = [...(config.sections ?? [])].sort((a, b) => a.order - b.order);

  const sectionsHtml = sections.map((section) => {
    const fields = [...section.fields].sort((a, b) => a.order - b.order);
    const fieldsHtml = fields.map((field) => {
      if (field.fieldType === 'repeatable_group') {
        const rows = (formState[field.schemaPath] as Record<string, unknown>[] | undefined) ?? [];
        if (rows.length === 0) return '';
        const subFields = [...(field.fields ?? [])].sort((a, b) => a.order - b.order);
        const rowsHtml = rows.map((row, idx) =>
          `<div class="row-entry"><strong>${field.label} — Entry ${idx + 1}</strong>${
            subFields.map((sf) =>
              `<div class="sub-field"><span class="label">${sf.label}:</span> <span class="value">${getDisplayValue(sf, row[sf.schemaPath || sf.id])}</span></div>`
            ).join('')
          }</div>`
        ).join('');
        return `<div class="field-group"><div class="field-label">${field.label}</div>${rowsHtml}</div>`;
      }
      return `<div class="field"><span class="field-label">${field.label}</span><span class="field-value">${getDisplayValue(field, formState[field.schemaPath])}</span></div>`;
    }).join('');
    return `<div class="section"><h3>${section.title}</h3>${fieldsHtml}</div>`;
  }).join('');

  const attachmentsHtml = attachments.length > 0
    ? `<div class="section"><h3>Attachments</h3>${
        attachments.map((f) =>
          `<div class="field"><span class="field-value">${f.name} (${formatFileSize(f.size)})</span></div>`
        ).join('')
      }</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${config.formName}</title>
  <style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;margin:0;padding:28px 36px}
    .header{border-bottom:2px solid #e5e7eb;padding-bottom:14px;margin-bottom:24px}
    h1{font-size:22px;margin:0 0 4px}
    .desc{color:#6b7280;font-size:13px;margin:0}
    .section{margin-bottom:28px}
    h3{font-size:14px;font-weight:600;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:8px;margin:0 0 12px}
    .field{display:flex;gap:8px;margin-bottom:10px;font-size:13px;line-height:1.5}
    .field-label{font-weight:600;color:#4b5563;min-width:180px;flex-shrink:0}
    .field-value{color:#111827;white-space:pre-wrap}
    .field-group{margin-bottom:12px}
    .row-entry{border:1px solid #e5e7eb;border-radius:6px;padding:10px 12px;margin-bottom:8px}
    .sub-field{font-size:12px;margin:4px 0}
    .label{font-weight:500;color:#6b7280;margin-right:4px}
    .value{color:#111827}
    @media print{body{padding:0}}
  </style>
</head>
<body>
  <div class="header">
    <h1>${config.formName}</h1>
    ${config.description ? `<p class="desc">${config.description}</p>` : ''}
  </div>
  ${sectionsHtml}
  ${attachmentsHtml}
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// File Upload Section
// ---------------------------------------------------------------------------

interface FileUploadSectionProps {
  attachments: UploadedFile[];
  onFilesAdded: (files: File[]) => void;
  onFileRemoved: (fileId: string) => void;
  disabled?: boolean;
}

function FileUploadSection({ attachments, onFilesAdded, onFileRemoved, disabled }: FileUploadSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
    onFilesAdded(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="mb-8">
      <p className="text-sm font-semibold text-gray-700 mb-2">Attachments</p>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onDragOver={(e) => { if (!disabled) { e.preventDefault(); setIsDragging(true); } }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click(); } }}
        className={`border-2 border-dashed rounded-lg px-6 py-8 text-center transition-colors ${
          disabled
            ? 'border-gray-200 bg-default-100 cursor-not-allowed opacity-60'
            : isDragging
            ? 'border-blue-400 bg-blue-50 cursor-pointer'
            : 'border-gray-300 bg-default-100 hover:bg-gray-100 cursor-pointer'
        }`}
      >
        <Icon icon="mdi:cloud-upload-outline" width={32} className="text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">
          Drag &amp; drop files here, or{' '}
          <span className="text-blue-600 font-medium">Browse</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">All file types accepted</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="*/*"
          disabled={disabled}
          className="hidden"
          onChange={(e) => {
            onFilesAdded(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </div>

      {attachments.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {attachments.map((f) => (
            <li
              key={f.fileId}
              className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon icon="mdi:file-outline" width={16} className="text-gray-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium truncate">{f.name}</span>
                <span className="text-gray-400 text-xs flex-shrink-0">({formatFileSize(f.size)})</span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onFileRemoved(f.fileId)}
                  className="ml-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  <Icon icon="mdi:close" width={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tag Input
// ---------------------------------------------------------------------------

interface TagInputProps {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

function TagInput({ value, onChange, placeholder, disabled }: TagInputProps) {
  const [input, setInput] = useState('');

  const addTag = () => {
    if (disabled) return;
    const tag = input.trim();
    if (tag && !value.includes(tag)) onChange([...value, tag]);
    setInput('');
  };

  return (
    <div className={`w-full border border-gray-300 rounded-lg px-3 py-2 flex flex-wrap gap-1.5 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white ${disabled ? 'opacity-60 bg-default-100' : ''}`}>
      {value.map((tag) => (
        <span key={tag} className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
          {tag}
          {!disabled && (
            <button type="button" onClick={() => onChange(value.filter((t) => t !== tag))}>
              <Icon icon="mdi:close" width={10} />
            </button>
          )}
        </span>
      ))}
      <input
        type="text"
        value={input}
        disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
        placeholder={value.length === 0 ? (placeholder ?? 'Type and press Enter') : ''}
        className="flex-1 min-w-[120px] outline-none text-sm text-gray-700 bg-transparent disabled:cursor-not-allowed"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multiselect — checkbox group
// ---------------------------------------------------------------------------

interface MultiselectProps {
  field: FormField;
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}

function MultiselectCheckboxGroup({ field, value, onChange, disabled }: MultiselectProps) {
  const toggle = (optValue: string) => {
    if (disabled) return;
    onChange(value.includes(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue]);
  };
  return (
    <div className="flex flex-col gap-1.5">
      {(field.options ?? []).map((opt) => (
        <label key={opt.value} className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={value.includes(opt.value)}
            disabled={disabled}
            onChange={() => toggle(opt.value)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
          />
          <span className="text-sm text-gray-700">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atomic field widget — renders the input control for a single field
// ---------------------------------------------------------------------------

interface FieldWidgetProps {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}

function FieldWidget({ field, value, onChange, errors, disabled }: FieldWidgetProps) {
  const inputClass =
    `w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 ${disabled ? 'opacity-60 bg-default-100 cursor-not-allowed' : ''}`;

  if (field.fieldType === 'repeatable_group') {
    const rows = (Array.isArray(value) ? value : []) as Record<string, unknown>[];
    const subFields = (field.fields ?? []).slice().sort((a, b) => a.order - b.order);

    const addRow = () => {
      if (disabled) return;
      const empty: Record<string, unknown> = {};
      subFields.forEach((sf) => { empty[sf.schemaPath || sf.id] = getDefaultFieldValue(sf); });
      onChange([...rows, empty]);
    };

    const removeRow = (idx: number) => { if (!disabled) onChange(rows.filter((_, i) => i !== idx)); };

    const updateCell = (rowIdx: number, path: string, val: unknown) => {
      const next = [...rows];
      next[rowIdx] = { ...next[rowIdx], [path]: val };
      onChange(next);
    };

    return (
      <div>
        {rows.map((row, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={rowIdx} className="border border-gray-200 rounded-lg p-4 mb-3">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {field.label} — Entry {rowIdx + 1}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeRow(rowIdx)}
                  className="text-xs text-danger hover:text-red-700 flex items-center gap-1"
                >
                  <Icon icon="mdi:trash-can-outline" width={14} />
                  Remove
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {subFields.map((sf) => {
                const sfPath = sf.schemaPath || sf.id;
                const sfErrKey = `${field.schemaPath}[${rowIdx}].${sfPath}`;
                return (
                  <div key={sf.id} data-schema-path={sfErrKey}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {sf.label}
                      {sf.required && <span className="text-danger ml-0.5">*</span>}
                    </label>
                    <FieldWidget
                      field={sf}
                      value={row[sfPath] ?? getDefaultFieldValue(sf)}
                      onChange={(v) => updateCell(rowIdx, sfPath, v)}
                      errors={errors}
                      disabled={disabled}
                    />
                    {errors[sfErrKey] && (
                      <p className="mt-1 text-xs text-danger">{errors[sfErrKey]}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <Icon icon="mdi:plus" width={16} />
            Add {field.label}
          </button>
        )}
      </div>
    );
  }

  if (field.fieldType === 'toggle') {
    return (
      <label className={`flex items-center gap-2 w-fit ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
        <div className="relative">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={disabled}
            onChange={(e) => !disabled && onChange(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors" />
          <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
        </div>
        <span className="text-sm text-gray-700">{value ? 'Yes' : 'No'}</span>
      </label>
    );
  }

  if (field.fieldType === 'select') {
    return (
      <select
        value={String(value ?? '')}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      >
        <option value="">{field.placeholder ?? `Select ${field.label}`}</option>
        {(field.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (field.fieldType === 'multiselect') {
    return (
      <MultiselectCheckboxGroup
        field={field}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (field.fieldType === 'tags') {
    return (
      <TagInput
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
        placeholder={field.placeholder}
        disabled={disabled}
      />
    );
  }

  const inputType =
    field.fieldType === 'datetime' ? 'datetime-local'
    : field.fieldType === 'date' ? 'date'
    : field.fieldType === 'number' ? 'number'
    : field.fieldType === 'email' ? 'email'
    : 'text';

  const extraProps: Record<string, number> = {};
  if (field.fieldType === 'number') {
    if (field.validation?.min !== undefined) extraProps.min = field.validation.min;
    if (field.validation?.max !== undefined) extraProps.max = field.validation.max;
  }

  return (
    <input
      type={inputType}
      value={String(value ?? '')}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder ?? ''}
      className={inputClass}
      {...extraProps}
    />
  );
}

// ---------------------------------------------------------------------------
// Field Renderer — label + widget + helpText + error
// ---------------------------------------------------------------------------

interface FieldRendererProps {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  errors: Record<string, string>;
  disabled?: boolean;
}

function FieldRenderer({ field, value, onChange, error, errors, disabled }: FieldRendererProps) {
  return (
    <div className="mb-5" data-schema-path={field.schemaPath}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {field.required && <span className="text-danger ml-0.5">*</span>}
      </label>

      <FieldWidget
        field={field}
        value={value}
        onChange={onChange}
        errors={errors}
        disabled={disabled}
      />

      {field.helpText && (
        <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-danger">{error}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form Submission Preview Overlay (user-facing mode)
// ---------------------------------------------------------------------------

interface FormSubmissionPreviewProps {
  config: FormConfig;
  formState: Record<string, unknown>;
  attachments: UploadedFile[];
  onClose: () => void;
}

function FormSubmissionPreview({ config, formState, attachments, onClose }: FormSubmissionPreviewProps) {
  const sections = [...(config.sections ?? [])].sort((a, b) => a.order - b.order);

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(generatePrintHtml(config, formState, attachments));
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:file-eye-outline" width={20} className="text-gray-600" />
          <span className="text-base font-semibold text-gray-900">Form Preview</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Icon icon="mdi:printer-outline" width={16} />
            Print / Save as PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Icon icon="mdi:arrow-left" width={16} />
            Back to Form
          </button>
        </div>
      </div>

      {/* Scrollable summary */}
      <div className="flex-1 overflow-y-auto px-8 py-6 max-w-3xl w-full mx-auto">
        {/* Form title */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{config.formName}</h2>
          {config.description && (
            <p className="text-sm text-gray-500 mt-1">{config.description}</p>
          )}
        </div>

        {/* Sections */}
        {sections.map((section) => {
          const fields = [...section.fields].sort((a, b) => a.order - b.order);
          return (
            <div key={section.id} className="mb-8">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
                {section.title}
              </h3>
              <div className="space-y-4">
                {fields.map((field) => {
                  if (field.fieldType === 'repeatable_group') {
                    const rows = (formState[field.schemaPath] as Record<string, unknown>[] | undefined) ?? [];
                    if (rows.length === 0) return null;
                    const subFields = [...(field.fields ?? [])].sort((a, b) => a.order - b.order);
                    return (
                      <div key={field.id}>
                        <p className="text-sm font-semibold text-gray-700 mb-2">{field.label}</p>
                        {rows.map((row, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={idx} className="border border-gray-200 rounded-lg p-4 mb-2 bg-default-100">
                            <p className="text-xs font-semibold text-gray-500 mb-3">Entry {idx + 1}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {subFields.map((sf) => (
                                <div key={sf.id}>
                                  <p className="text-xs text-gray-500 mb-0.5">{sf.label}</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {getDisplayValue(sf, row[sf.schemaPath || sf.id])}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <div key={field.id} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                      <span className="text-sm text-gray-500 sm:min-w-[200px] sm:flex-shrink-0">{field.label}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {getDisplayValue(field, formState[field.schemaPath])}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
              Attachments
            </h3>
            <ul className="space-y-2">
              {attachments.map((f) => (
                <li key={f.fileId} className="flex items-center gap-2 text-sm">
                  <Icon icon="mdi:file-outline" width={16} className="text-gray-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{f.name}</span>
                  <span className="text-gray-400">({formatFileSize(f.size)})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success banner
// ---------------------------------------------------------------------------

function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <Icon icon="mdi:check-circle" width={40} className="text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Form Submitted</h3>
      <p className="text-sm text-gray-600 max-w-md">{message || 'Your form has been submitted successfully.'}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main FormRenderer
// ---------------------------------------------------------------------------

export function FormRenderer({ config, uploadFile, removeFile, submitForm, usageId, hideActions = false, className = '' }: FormRendererProps) {
  const [formState, setFormState] = useState<Record<string, unknown>>(() => initFormState(config));
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear clipboard feedback timer on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleShare = useCallback(() => {
    if (!usageId) return;
    const url = getPalFormUrl(config.formName, usageId);
    navigator.clipboard.writeText(url).then(() => {
      setIsCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
    }).catch(() => {
      // Clipboard API unavailable (e.g. non-secure context) — no-op
    });
  }, [usageId, config.formName]);

  // -------------------------------------------------------------------------
  // Change handler — clears this field's error on edit
  // -------------------------------------------------------------------------
  const handleChange = (schemaPath: string, value: unknown) => {
    setFormState((prev) => ({ ...prev, [schemaPath]: value }));
    if (errors[schemaPath]) {
      setErrors((prev) => { const e = { ...prev }; delete e[schemaPath]; return e; });
    }
  };

  // -------------------------------------------------------------------------
  // File handlers
  // -------------------------------------------------------------------------
  const handleFilesAdded = (files: File[]) => {
    files.forEach((file) => {
      const fileId = `${Date.now()}_${file.name}`;
      const entry: UploadedFile = {
        fileId, file, name: file.name, size: file.size, type: file.type, lastModified: file.lastModified,
      };
      setAttachments((prev) => [...prev, entry]);
      uploadFile({
        file,
        metadata: { name: file.name, size: file.size, type: file.type, lastModified: file.lastModified },
        formState,
      });
    });
  };

  const handleFileRemoved = (fileId: string) => {
    setAttachments((prev) => prev.filter((f) => f.fileId !== fileId));
    removeFile({ fileId, formState });
  };

  // -------------------------------------------------------------------------
  // Validation — runs only on submit
  // -------------------------------------------------------------------------
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    for (const section of config.sections ?? []) {
      for (const field of section.fields ?? []) {
        const value = formState[field.schemaPath];
        const isEmpty =
          value === '' || value === null || value === undefined ||
          (Array.isArray(value) && value.length === 0);

        if (field.required && isEmpty) {
          newErrors[field.schemaPath] = `${field.label} is required.`;
        }

        if (!isEmpty && field.validation) {
          const v = field.validation;
          const str = String(value);
          if (v.minLength && str.length < v.minLength) {
            newErrors[field.schemaPath] = `${field.label} must be at least ${v.minLength} characters.`;
          } else if (v.maxLength && str.length > v.maxLength) {
            newErrors[field.schemaPath] = `${field.label} must be no more than ${v.maxLength} characters.`;
          } else if (v.min !== undefined && Number(value) < v.min) {
            newErrors[field.schemaPath] = `${field.label} must be at least ${v.min}.`;
          } else if (v.max !== undefined && Number(value) > v.max) {
            newErrors[field.schemaPath] = `${field.label} must be no more than ${v.max}.`;
          } else if (v.pattern) {
            // Guard against ReDoS from user-supplied patterns stored in FormConfig
            try {
              if (!new RegExp(v.pattern).test(str)) {
                newErrors[field.schemaPath] = `${field.label} format is invalid.`;
              }
            } catch {
              // Malformed pattern in FormConfig — skip regex validation
            }
          }
        }

        // Validate repeatable group sub-fields
        if (field.fieldType === 'repeatable_group') {
          const rows = (formState[field.schemaPath] as Record<string, unknown>[]) ?? [];
          rows.forEach((row, rowIdx) => {
            (field.fields ?? []).forEach((sf) => {
              const sfPath = sf.schemaPath || sf.id;
              const sfVal = row[sfPath];
              const sfEmpty =
                sfVal === '' || sfVal === null || sfVal === undefined ||
                (Array.isArray(sfVal) && sfVal.length === 0);
              if (sf.required && sfEmpty) {
                newErrors[`${field.schemaPath}[${rowIdx}].${sfPath}`] = `${sf.label} is required.`;
              }
            });
          });
        }
      }
    }

    setErrors(newErrors);

    // Scroll to first error field
    const firstErrorKey = Object.keys(newErrors)[0];
    if (firstErrorKey) {
      document.querySelector(`[data-schema-path="${firstErrorKey}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return Object.keys(newErrors).length === 0;
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const handleSubmit = () => {
    if (validate()) {
      submitForm({ formState, attachments });
      setSubmitted(true);
    }
  };

  // -------------------------------------------------------------------------
  // Preview overlay (user-facing mode)
  // -------------------------------------------------------------------------
  if (isPreviewOpen) {
    return (
      <FormSubmissionPreview
        config={config}
        formState={formState}
        attachments={attachments}
        onClose={() => setIsPreviewOpen(false)}
      />
    );
  }

  // -------------------------------------------------------------------------
  // Success state
  // -------------------------------------------------------------------------
  if (submitted) {
    return (
      <div className={`flex flex-col bg-white ${className}`}>
        <SuccessBanner message={config.successMessage} />
      </div>
    );
  }

  const sections = [...(config.sections ?? [])].sort((a, b) => a.order - b.order);
  const disabled = !isEditMode;

  return (
    <div className={`flex flex-col bg-white ${className}`}>
      {/* Form header */}
      <div className="px-6 py-5 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          {/* Left: form title & meta */}
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900">{config.formName}</h2>
            {config.description && (
              <p className="text-sm text-gray-500 mt-1">{config.description}</p>
            )}
            <div className="flex gap-3 mt-2">
              {config.schemaId && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Icon icon="mdi:identifier" width={11} /> {config.schemaId}
                </span>
              )}
              {config.schemaVersion && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Icon icon="mdi:tag-outline" width={11} /> v{config.schemaVersion}
                </span>
              )}
            </div>
          </div>

          {/* Right: Edit/Preview toggle + Share — hidden in user-facing mode */}
          {!hideActions && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-1 p-0.5 bg-default-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => setIsEditMode(true)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    isEditMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditMode(false)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    !isEditMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Preview
                </button>
              </div>

              {usageId && (
                <button
                  type="button"
                  onClick={handleShare}
                  title="Copy form link"
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
                    isCopied
                      ? 'border-green-300 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Icon icon={isCopied ? 'mdi:check' : 'mdi:share-variant-outline'} width={14} />
                  {isCopied ? 'Copied!' : 'Share'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <FileUploadSection
          attachments={attachments}
          onFilesAdded={handleFilesAdded}
          onFileRemoved={handleFileRemoved}
          disabled={disabled}
        />

        {sections.map((section) => (
          <div key={section.id} className="mb-8">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-800">{section.title}</h3>
              {section.description && (
                <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
              )}
            </div>
            <div>
              {[...section.fields]
                .sort((a, b) => a.order - b.order)
                .map((field) => (
                  <FieldRenderer
                    key={field.id}
                    field={field}
                    value={formState[field.schemaPath]}
                    onChange={(v) => handleChange(field.schemaPath, v)}
                    error={errors[field.schemaPath]}
                    errors={errors}
                    disabled={disabled}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* Submit footer */}
      <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
        {Object.keys(errors).length > 0 && (
          <p className="text-xs text-danger flex items-center gap-1">
            <Icon icon="mdi:alert-circle-outline" width={14} />
            Please fix the errors above before submitting.
          </p>
        )}
        <div className="ml-auto flex items-center gap-2">
          {hideActions && (
            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon icon="mdi:file-eye-outline" width={16} />
              Preview
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled}
            className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors ${
              disabled
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {config.submitLabel || 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FormRenderer;
